import { decodeFunctionResult, encodeFunctionData, type Hex } from "viem";
import { ponsRewardNftABI } from "../../../abis/pons-reward-nft";
import { currentActionFacts } from "../../../api/action-capabilities";
import type { BootstrapResponse } from "../../../api/schemas";
import { REFERRAL_FINALIZED_RECEIPT_PROTOCOL, type ReferralMintEvidence } from "../contracts/referral-mint";
import { verifyReferralProgram } from "../contracts/referral-program";
import type { Eip1193Provider } from "../../../wallet/eip1193";
import { readRpc, type RpcReadOptions } from "../../../wallet/read-rpc";
import { observeRedeemDeployment } from "../../../wallet/redeem-fee-route";
import { ELYSIUM, isElysium, elysiumWriteBlock } from "../../../config/elysium";

// Admission comes from the recorded 2026-10-02 referral upgrade and its source,
// never from a prepared call or from whichever implementation happens to exist.
// contracts/deployments/robinhood-mainnet.proxy-v1.json + mainnet-referral-mint-upgrade-v1.json.
export const ROBINHOOD_REFERRAL_DEPLOYMENT = Object.freeze({
  chainId: 4663,
  collection: "0x0D5d2647D4410B8FF6343310BEba0fd8F3971b1B",
  implementation: "0xFb54d890fA1CF1988433a5162D64677369e8D8B5",
  sourceToken: "0xab55722a7d2D0FAe10DB44B914dDf6a307883bF2",
  signer: "0xE229462E2445D8093D5BE252e7f51386F1655409",
  instanceId: "0x9fdcec3b41e84eb9deab7a50893a3929146e09f04e01d117a28c2884140a30e6",
  policyHash: "0x6643f984594f0bb73e5e2a36d03f580a6ede9942383be2b139c9057a4ae8bfa2",
  policyVersion: "1",
} as const);
const same = (a: unknown, b: string) => typeof a === "string" && a.toLowerCase() === b.toLowerCase();
const ELYSIUM_REFERRAL_DEPLOYMENT = Object.freeze({ chainId: ELYSIUM.chainId,
  collection: ELYSIUM.collection, implementation: ELYSIUM.implementation, sourceToken: ELYSIUM.token,
  signer: ELYSIUM.signer, instanceId: ELYSIUM.instanceId,
  policyHash: ELYSIUM.freeMintPolicyHash, policyVersion: ELYSIUM.freeMintPolicyVersion });
const referralDeployment = (bootstrap: BootstrapResponse) => isElysium(bootstrap) ?
  ELYSIUM_REFERRAL_DEPLOYMENT : ROBINHOOD_REFERRAL_DEPLOYMENT;

export function referralDeploymentReason(bootstrap: BootstrapResponse | null, sourceToken: string | null): string | null {
  const v3Block = elysiumWriteBlock(bootstrap);
  if (v3Block) return v3Block;
  if (!bootstrap || !sourceToken) return "Referral deployment/source unavailable. Refresh the application.";
  const facts = currentActionFacts(bootstrap);
  if (!facts.writes || !facts.execution || !facts.deployment) return "Referral execution blocked by registered bootstrap write/execution gates";
  const admitted = referralDeployment(bootstrap);
  if (bootstrap.environment !== "production" || bootstrap.forkTestOnly || bootstrap.contracts.mintChainId !== admitted.chainId ||
    !same(bootstrap.contracts.collectionAddress, admitted.collection) || !same(bootstrap.contracts.authorizationSignerAddress, admitted.signer) ||
    !same(sourceToken, admitted.sourceToken) || bootstrap.deployment.sourceTokenStatus !== "REGISTERED" ||
    !bootstrap.tokens.some(token => token.tracking && token.chainId === admitted.chainId && same(token.tokenAddress, sourceToken))) {
    return "The configured referral deployment, signer or source does not match the admitted deployment.";
  }
  return null;
}

export async function resolveReferralMintEvidence(provider: Eip1193Provider, bootstrap: BootstrapResponse,
  sourceToken: string, assertCurrent: () => void, options: RpcReadOptions = {}): Promise<ReferralMintEvidence> {
  const guard = () => { options.signal?.throwIfAborted(); assertCurrent(); };
  guard();
  const reason = referralDeploymentReason(bootstrap, sourceToken);
  if (reason) throw new Error(reason);
  const admitted = referralDeployment(bootstrap);
  const observe = async (rpc: Eip1193Provider, check: () => void, readOptions: RpcReadOptions) => {
    const request = async (method: "eth_chainId" | "eth_getBlockByNumber" | "eth_call", params: readonly unknown[]) => {
      check(); const result = await readRpc(rpc, { method, params }, readOptions); check(); return result;
    };
    const checkChain = async () => {
      const chain = await request("eth_chainId", []);
      if (typeof chain !== "string" || !/^0x[0-9a-f]+$/i.test(chain) || BigInt(chain) !== BigInt(admitted.chainId)) {
        throw new Error(`Switch to chain ${admitted.chainId}, then review referral mint again.`);
      }
    };
    await checkChain();
    const rawBlock = await request("eth_getBlockByNumber", ["latest", false]);
    const block = rawBlock && typeof rawBlock === "object" ? rawBlock as Record<string, unknown> : {};
    if (typeof block.hash !== "string" || !/^0x[0-9a-f]{64}$/i.test(block.hash) || BigInt(block.hash) === 0n ||
      typeof block.number !== "string" || !/^0x(?:0|[1-9a-f][0-9a-f]*)$/i.test(block.number)) throw new Error("Invalid referral deployment block");
    const pin = { blockHash: block.hash as Hex, requireCanonical: true } as const;
    const read = async (functionName: "INSTANCE_ID" | "AUTHORIZATION_SIGNER" | "REWARD_POLICY_HASH" | "LEGACY_REWARD_POLICY_HASH" | "SOCIAL_REWARD_POLICY_HASH" | "REWARD_POLICY_VERSION") => {
      const raw = await request("eth_call", [{ to: admitted.collection, data: encodeFunctionData({ abi: ponsRewardNftABI, functionName }) }, pin]);
      if (typeof raw !== "string" || !/^0x[0-9a-f]{64}$/i.test(raw)) throw new Error("Invalid referral deployment binding response");
      return decodeFunctionResult({ abi: ponsRewardNftABI, functionName, data: raw as Hex });
    };
    const [deployment, instance, signer, policy, version, socialPolicy] = await Promise.all([
      observeRedeemDeployment(rpc, admitted.collection, pin, check, readOptions),
      read("INSTANCE_ID"), read("AUTHORIZATION_SIGNER"),
      read(isElysium(bootstrap) ? "LEGACY_REWARD_POLICY_HASH" : "REWARD_POLICY_HASH"), read("REWARD_POLICY_VERSION"),
      isElysium(bootstrap) ? read("SOCIAL_REWARD_POLICY_HASH") : Promise.resolve(null),
    ]);
    if (!same(deployment.implementation, admitted.implementation) || !deployment.implementationCodeHash ||
      isElysium(bootstrap) && (!same(deployment.implementationCodeHash, ELYSIUM.implementationCodeHash) ||
        !same(socialPolicy, ELYSIUM.socialRewardPolicyHash)) ||
      !same(String(instance), admitted.instanceId) || !same(String(signer), admitted.signer) ||
      !same(String(policy), admitted.policyHash) || String(version) !== (isElysium(bootstrap) ? ELYSIUM.policyVersion : admitted.policyVersion)) {
      throw new Error("Referral implementation, instance, signer or reward policy differs from the admitted deployment. Refresh and review again.");
    }
    const recheck = await request("eth_getBlockByNumber", [block.number, false]);
    if (!recheck || typeof recheck !== "object" || !same((recheck as Record<string, unknown>).hash, block.hash)) {
      throw new Error("Referral deployment snapshot changed. Review again.");
    }
    await checkChain(); check();
    return deployment;
  };
  const observed = await observe(provider, guard, options);
  guard();
  const evidence: ReferralMintEvidence = {
    ...admitted, environment: `${bootstrap.environment}:${bootstrap.capabilities.mintGatewayMode}`,
    ...(isElysium(bootstrap) ? { policyHashes: [ELYSIUM.freeMintPolicyHash, ELYSIUM.socialRewardPolicyHash] } : {}),
    evidenceId: `${isElysium(bootstrap) ? "ELYSIUM" : "ROBINHOOD"}_REFERRAL_V1:${admitted.implementation}:${observed.collectionCodeHash}:${observed.implementationCodeHash}`,
    executionEnabled: true, collectionCodeHash: observed.collectionCodeHash, receiptProtocol: REFERRAL_FINALIZED_RECEIPT_PROTOCOL,
    verifySignedReferral: verifyReferralProgram,
    verifyDeployment: async (rpc, check, readOptions) => {
      const current = await observe(rpc, check, readOptions);
      if (current.collectionCodeHash !== observed.collectionCodeHash || current.implementationCodeHash !== observed.implementationCodeHash) {
        throw new Error("Referral contract code changed. Prepare and review again.");
      }
    },
  };
  return Object.freeze(evidence);
}
